import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Plus, FileText, Eye, MousePointerClick, TrendingUp, Inbox,
  PlusCircle, LayoutTemplate, Code2, Share2, ChevronRight, MoreHorizontal, LineChart,
} from "lucide-react";
import { motion } from "framer-motion";
import { getStoreAnalytics, getForms, getStoreInfo } from "../services/api";
import { mlGet } from "../components/form-builder/types";
import { StoreAnalytics, StoreDailyPoint } from "../types/dashboard";
import { Card, Button, EmptyState, FormStatusBadge, SubmissionStatusBadge, VideoGuideButton } from "../components/ui";
import { SubmissionsAreaChart } from "../components/charts/SubmissionsAreaChart";
import { StatusDonutChart } from "../components/charts/StatusDonutChart";
import { Sparkline } from "../components/charts/Sparkline";
import { StatTile } from "../components/ui/StatTile";
import { greeting, timeAgo, pickFormIcon, avatarTone, pctChange } from "../utils/format";

const RANGE_OPTIONS = [
  { id: "7", label: "Last 7 Days" },
  { id: "30", label: "Last 30 Days" },
  { id: "90", label: "Last 90 Days" },
];

const STATUS_PILLS = ["all", "active", "draft", "archived"] as const;

function sumBy(rows: StoreDailyPoint[], key: keyof StoreDailyPoint): number {
  return rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
}

const ACTIVITY_ICON_TONE: Record<string, string> = {
  new: "bg-brand-100 text-brand-600",
  approved: "bg-success-100 text-success-600",
  rejected: "bg-danger-100 text-danger-600",
  pending: "bg-warning-100 text-warning-600",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [days, setDays] = useState("30");
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_PILLS)[number]>("all");
  const daysNum = Number(days);

  const { data, isLoading } = useQuery({
    queryKey: ["store-analytics-window", days],
    queryFn: () => getStoreAnalytics(daysNum * 2).then((r) => r.data as StoreAnalytics),
  });

  const { data: forms, isLoading: formsLoading } = useQuery({
    queryKey: ["forms"],
    queryFn: () => getForms().then((r) => r.data.forms || []),
  });

  const { data: storeInfo } = useQuery({
    queryKey: ["store-settings"],
    queryFn: () => getStoreInfo().then((r) => r.data.store),
  });

  const storeName: string = storeInfo?.store_name?.split(" ")[0] || "there";

  const windows = useMemo(() => {
    const all = data?.daily ?? [];
    const half = Math.floor(all.length / 2);
    const previous = all.slice(0, half);
    const current = all.slice(half);

    const metric = (key: keyof StoreDailyPoint) => ({
      current: sumBy(current, key),
      previous: sumBy(previous, key),
      trend: current.map((d) => Number(d[key]) || 0),
    });

    const views = metric("views");
    const starts = metric("starts");
    const submissions = metric("submissions");

    const convCurrent = views.current > 0 ? (submissions.current / views.current) * 100 : 0;
    const convPrevious = views.previous > 0 ? (submissions.previous / views.previous) * 100 : 0;

    return { current, views, starts, submissions, convCurrent, convPrevious };
  }, [data]);

  const trendByFormId = useMemo(() => {
    const map = new Map<number, number[]>();
    (data?.top_forms ?? []).forEach((f) => map.set(f.id, f.trend));
    return map;
  }, [data]);

  const filteredForms = useMemo(() => {
    const list = [...(forms || [])].sort(
      (a: any, b: any) => new Date(b.updated_at ?? b.created_at).getTime() - new Date(a.updated_at ?? a.created_at).getTime()
    );
    if (statusFilter === "all") return list;
    return list.filter((f: any) => f.status === statusFilter);
  }, [forms, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: forms?.length ?? 0, active: 0, draft: 0, archived: 0 };
    (forms || []).forEach((f: any) => {
      if (f.status in counts) counts[f.status]++;
    });
    return counts;
  }, [forms]);

  const totalForms = data?.summary.total_forms ?? forms?.length ?? 0;
  const noData = !isLoading && !formsLoading && totalForms === 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      <div
        className="flex flex-wrap items-center justify-between gap-4 mb-6 rounded-2xl p-6 border border-brand-100"
        style={{ background: "linear-gradient(135deg, var(--brand-50) 0%, var(--brand-100) 45%, #FFD3BE 100%)" }}
      >
        <div>
          <h1 className="text-xl font-bold text-ink-900 font-sora">
            {greeting()}, {storeName}! 👋
          </h1>
          <p className="text-xs text-brand-700 font-sans mt-1">
            Create beautiful forms, collect responses and grow your business.
          </p>
        </div>
        <Button icon={Plus} onClick={() => navigate("/forms")}>Create New Form</Button>
      </div>

      {noData ? (
        <Card>
          <EmptyState
            icon={FileText}
            title="No forms yet"
            description="Create your first form to start collecting responses and see analytics here."
            action={{ label: "Create your first form", onClick: () => navigate("/forms") }}
          />
        </Card>
      ) : (
        <motion.div className="space-y-6" initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.05 } } }}>
          {/* Stat tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile
              label="Total Views"
              value={windows.views.current}
              icon={Eye}
              tone="brand"
              loading={isLoading}
              trend={windows.views.trend}
              delta={pctChange(windows.views.current, windows.views.previous)}
              deltaLabel={`vs prev ${days}d`}
            />
            <StatTile
              label="Total Responses"
              value={windows.submissions.current}
              icon={Inbox}
              tone="neutral"
              loading={isLoading}
              trend={windows.submissions.trend}
              delta={pctChange(windows.submissions.current, windows.submissions.previous)}
              deltaLabel={`vs prev ${days}d`}
            />
            <StatTile
              label="Conversion Rate"
              value={`${windows.convCurrent.toFixed(1)}%`}
              icon={TrendingUp}
              tone="success"
              loading={isLoading}
              delta={pctChange(windows.convCurrent, windows.convPrevious)}
              deltaLabel={`vs prev ${days}d`}
            />
            <StatTile
              label="Form Starts"
              value={windows.starts.current}
              icon={MousePointerClick}
              tone="warning"
              loading={isLoading}
              trend={windows.starts.trend}
              delta={pctChange(windows.starts.current, windows.starts.previous)}
              deltaLabel={`vs prev ${days}d`}
            />
          </div>

          {/* Your Forms + Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card padding="none" className="lg:col-span-2 overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 pb-3">
                <h2 className="text-sm font-bold text-blackDark font-dmsans">Your Forms</h2>
                <div className="flex items-center gap-1 p-1 bg-ink-100 rounded-lg">
                  {STATUS_PILLS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className={`relative flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium font-sans rounded-md capitalize transition-colors z-0 ${statusFilter === s ? "text-white" : "text-ink-500 hover:text-ink-900"
                        }`}
                    >
                      {statusFilter === s && (
                        <motion.div
                          layoutId="status-filter"
                          className="absolute inset-0 bg-brand-500 rounded-md shadow-sm z-[-1]"
                          initial={false}
                          transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        />
                      )}
                      <span className="relative z-10">{s}</span>
                      <span className="relative z-10 text-[10px] opacity-80">{statusCounts[s] ?? 0}</span>
                    </button>
                  ))}
                </div>
              </div>

              {filteredForms.length === 0 && !formsLoading ? (
                <EmptyState icon={FileText} title="No forms match this filter" />
              ) : (
                <div className="divide-y divide-ink-100">
                  {filteredForms.slice(0, 5).map((f: any, i: number) => {
                    const name = mlGet(f.name, "en");
                    const desc = mlGet(f.description, "en");
                    const Icon = pickFormIcon(name);
                    const trend = trendByFormId.get(f.id);
                    return (
                      <div
                        key={f.id}
                        onClick={() => navigate("/forms")}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-ink-100/60 cursor-pointer transition-colors"
                      >
                        <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${avatarTone(i)}`}>
                          <Icon className="h-4.5 w-4.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-ink-900 font-sans truncate">{name || "Untitled"}</p>
                          {desc && <p className="text-xs text-ink-500 font-sans truncate">{desc}</p>}
                        </div>
                        <div className="text-right flex-shrink-0 hidden sm:block">
                          <p className="text-sm font-semibold text-ink-900 font-sora tabular-nums">{f.submissions_count ?? 0}</p>
                          <p className="text-[11px] text-ink-500 font-sans">responses</p>
                        </div>
                        <div className="w-16 flex-shrink-0 hidden md:block">
                          {trend && trend.length > 0 && <Sparkline data={trend} color="var(--brand-500)" width={64} height={24} />}
                        </div>
                        <FormStatusBadge status={f.status} />
                        <span className="text-[11px] text-ink-500 font-sans flex-shrink-0 hidden lg:block w-20 text-right">
                          {timeAgo(f.updated_at ?? f.created_at)}
                        </span>
                        <MoreHorizontal className="h-4 w-4 text-ink-500 flex-shrink-0 hidden sm:block" />
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="px-4 py-3 border-t border-borderGreyColor">
                <button
                  onClick={() => navigate("/forms")}
                  className="flex items-center gap-1 text-xs font-semibold text-[#ed5a29] font-dmsans hover:underline"
                >
                  View all forms <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </Card>

            <Card padding="none">
              <div className="flex items-center justify-between p-4 pb-3">
                <h2 className="text-sm font-bold text-blackDark font-dmsans">Recent Activity</h2>
              </div>
              {(data?.recent_submissions ?? []).length === 0 && !isLoading ? (
                <EmptyState icon={Inbox} title="No activity yet" description="New submissions will show up here as they come in." />
              ) : (
                <div className="divide-y divide-ink-100">
                  {(data?.recent_submissions ?? []).slice(0, 6).map((s) => (
                    <div key={s.id} className="flex items-start gap-3 px-4 py-3">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${ACTIVITY_ICON_TONE[s.status] ?? "bg-ink-100 text-ink-500"}`}>
                        <Inbox className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-ink-900 font-sans leading-snug">
                          <span className="font-semibold">{s.customer_email || "Anonymous"}</span> submitted{" "}
                          <span className="font-semibold">{mlGet(s.form_name, "en") || "a form"}</span>
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <SubmissionStatusBadge status={s.status} />
                          <span className="text-[11px] text-ink-500 font-sans">{timeAgo(s.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Submissions over time + status breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-bold text-blackDark font-dmsans">Submissions Over Time</h2>
                <select
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="text-xs font-medium text-blackDark font-dmsans border border-borderGreyColor rounded-lg px-2.5 py-1.5 bg-white outline-none"
                >
                  {RANGE_OPTIONS.map((r) => (
                    <option key={r.id} value={r.id}>{r.label}</option>
                  ))}
                </select>
              </div>
              {isLoading ? (
                <div className="h-[260px] flex items-center justify-center">
                  <div className="h-5 w-5 rounded-full border-2 border-[#ed5a29] border-t-transparent animate-spin" />
                </div>
              ) : windows.submissions.current === 0 ? (
                <div className="h-[260px] flex items-center justify-center">
                  <EmptyState
                    icon={LineChart}
                    title="No submissions yet"
                    description="Once your forms start receiving responses, the trend shows up here."
                  />
                </div>
              ) : (
                <SubmissionsAreaChart data={windows.current.map((d) => ({ date: d.date, submissions: d.submissions }))} />
              )}
            </Card>

            <Card>
              <h2 className="text-sm font-bold text-blackDark font-dmsans mb-4">Submissions by Status</h2>
              {isLoading ? (
                <div className="h-[260px] flex items-center justify-center">
                  <div className="h-5 w-5 rounded-full border-2 border-[#ed5a29] border-t-transparent animate-spin" />
                </div>
              ) : (
                <StatusDonutChart data={data?.submissions_by_status ?? {}} />
              )}
            </Card>
          </div>

          {/* Quick actions */}
          <Card>
            <h2 className="text-sm font-bold text-ink-900 font-sans mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Create Form", icon: PlusCircle, tone: "bg-brand-100 text-brand-600", to: "/forms" },
                { label: "Browse Templates", icon: LayoutTemplate, tone: "bg-info-100 text-info-600", to: "/forms" },
                { label: "Embed Form", icon: Code2, tone: "bg-success-100 text-success-600", to: "/settings" },
                { label: "Share Form", icon: Share2, tone: "bg-warning-100 text-warning-600", to: "/settings" },
              ].map((a) => (
                <button
                  key={a.label}
                  onClick={() => navigate(a.to)}
                  className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl border border-ink-100 hover:border-brand-400 hover:bg-brand-50 hover:shadow-sm transition-all duration-150"
                >
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${a.tone}`}>
                    <a.icon className="h-4 w-4" />
                  </div>
                  <span className="text-xs font-medium text-ink-900 font-sans">{a.label}</span>
                </button>
              ))}
            </div>
          </Card>
        </motion.div>
      )}
      <VideoGuideButton />
    </div>
  );
}